import {Component, ViewChild} from "@angular/core";
import {ActivatedRoute, Router} from "@angular/router";
import {SiteService} from "../../sites/site.service";
import {SiteCdfModel, SiteDetailModel, siteDetailModelFactory} from "../../sites/site-detail.model";
import {WorkflowStep} from "../workflow-step";
import {WorkflowStepStatus} from "../models/workflow-step-status.enum";
import {WorkflowService} from "../workflow.service";
import {DialogService} from "../../bootstrap-modal/dialog.service";
import {SelectK2SiteNameComponent} from "./select-k2-site-name-dialog.component";
import {exposeComponentInDebugMode} from "../../misc/expose-component-in-debug-mode.decorator";
import {handleErrors} from "../../misc/handle-errors.decorator";
import {afterRun} from "../../misc/after-run.decorator";
import {contactTypes} from "../../types/contact";
import {UploaderDialogResult} from "../../uploader-wrapper/uploader-dialog.model";
import {HttpDownloadService} from "../../http/http-download.service";
import {ProjectService} from "../../project/project.service";
import {isEqual} from "lodash";
import {SiteCdfDialogComponent} from "./site-cdf-dialog.component";
import {ModifyContactDialogComponent} from "../../contact/modify-contact-dialog.component";
import {ContactService} from "../../contact/contact.service";
import {AreaGroupService} from "../../area-group/area-group.service";
import {WorkflowTargetInfoModel} from "../models/workflow-target-info.model";

@Component({
  templateUrl: "./site-details.template.pug",
  providers: [
    SiteService,
    ProjectService,
    AreaGroupService,
  ],
})
@exposeComponentInDebugMode()
export class SiteDetailsComponent extends WorkflowStep {
  k2Sites: any[] = [];
  site: SiteDetailModel = siteDetailModelFactory();
  originalSite: SiteDetailModel = siteDetailModelFactory();
  projectId: string;
  selectedK2Site: string;
  contactTypes: any[] = contactTypes;
  hasContacts: boolean = false;
  error: Error;
  isLegacy: boolean = true;
  groupOptions: { name: string, id: string }[];

  @ViewChild("siteForm") siteForm;
  private fields: string[] = [
    "name",
    "k2Name",
    "siteCode",
    "estimatedStartDate",
    "estimatedCompleteDate",
    "approvedDate",
    "hardwareOrderDate",
    "deliveryDate",
    "installDate",
    "submissionDate",
    "completionDate",
    "eotLeaseDate",
    "sourceName",
    "sourceDocumentationLink",
    "estimatedSiteSowSigned",
    "actualSiteSowSigned",
    "estimatedSiteDiscovery",
    "actualSiteDiscovery",
    "estimatedSiteDesignApproval",
    "actualSiteDesignApproval",
    "estimatedSiteMonitoringEnabled",
    "actualSiteMonitoringEnabled",
    "estimatedSiteReadyToDeploy",
    "actualSiteReadyToDeploy",
    "estimatedSiteDeploymentStart",
    "actualSiteDeploymentStart",
    "estimatedSiteDeploymentEnd",
    "actualSiteDeploymentEnd",
    "estimatedSiteCustomerAcceptance",
    "actualSiteCustomerAcceptance",
    "estimatedSiteHandover",
    "actualSiteHandover",
    "businessPartnerId",
    "defaultAreaGroup",
  ];

  private async loadPartnerPurchaseOrders(sitePartnerPurchaseOrders = []) {
    // get the Project PPOs
    const projectPartnerPOs =
      await this.projectService.getPartnerPurchaseOrders(this.workflowService.workflowConfig.projectId);
    // Compare Site & project PPOs and update sites as needed
    const mergedPPOs = [];
    projectPartnerPOs.forEach((projectPPO) => {
      const sitePPOIndex = sitePartnerPurchaseOrders.findIndex(sitePPO => sitePPO.name === projectPPO.name);
      mergedPPOs.push({name: projectPPO.name, checked: (sitePPOIndex >= 0)});
    });
    // return the merged Partner PPOs
    return mergedPPOs;
  }

  @afterRun((self) => { self.loading = false; })
  @handleErrors()
  async loadData(): Promise<void> {
    this.loading = true;

    const siteId = this.workflowService.workflowConfig.targetId;

    const site = await this.siteService.getSiteById(siteId);

    this.isLegacy = this.workflowService.workflowConfig.targetSystem === "legacy";
    this.originalSite = siteDetailModelFactory(site);
    this.site = site;
    this.site.partnerPurchaseOrders = await this.loadPartnerPurchaseOrders(this.site.partnerPurchaseOrders);
    this.k2Sites = await this.siteService.getK2SitesByTTSiteId(siteId);
    this.hasContacts = this.contactTypes.some(type => this.site[type.field]);
    const {groups} = await this.areaGroupService.getGroupsByProjectId(this.projectId);
    this.groupOptions = groups.map(item => ({
      name: `${item.areaName} - ${item.groupName}`,
      id: item.id,
    }));
  }

  @afterRun((self) => { self.loading = false; })
  @handleErrors()
  async loadContacts(): Promise<void> {
    this.loading = true;

    const siteId = this.workflowService.workflowConfig.targetId;
    const site = await this.siteService.getSiteById(siteId);
    this.contactTypes.forEach(({field}) => {
      // Copy over only the new contacts
      this.site[field] = site[field];
    });
    this.hasContacts = this.contactTypes.some(type => this.site[type.field]);
  }

  async ready(workflowTargetInfo: WorkflowTargetInfoModel) {
    this.projectId = workflowTargetInfo.projectId;
    this.loadData();
  }

  constructor(
    private siteService: SiteService,
    private projectService: ProjectService,
    public route: ActivatedRoute,
    public router: Router,
    public workflowService: WorkflowService,
    public dialogService: DialogService,
    private httpDownloadService: HttpDownloadService,
    private contactService: ContactService,
    private areaGroupService: AreaGroupService,
  ) {
    super(true);
  }

  @handleErrors()
  async autoSave(): Promise<boolean> {
    this.site.partnerPurchaseOrders = this.site.partnerPurchaseOrders
      .filter(sPPO => sPPO.checked);

    await this.siteService.saveSite(this.site, this.selectedK2Site);

    await this.workflowService.setStepStatus(WorkflowStepStatus.Completed);

    return true;
  }

  private checkPartnerPurchaseOrdersAreDirty() {
    const newPO = this.site.partnerPurchaseOrders.filter(sPPO => sPPO.checked)
      .map(ppo => ppo.name);
    const oldPO = this.originalSite.partnerPurchaseOrders
      .map(ppo => ppo.name);
    return !isEqual(newPO, oldPO);
  }

  private checkCdfsAreDirty() {
    return !isEqual(this.site.cdfs, this.originalSite.cdfs);
  }

  checkDirty(): boolean {
    return this.fields.some(field => this.site[field] !== this.originalSite[field]) ||
      this.checkPartnerPurchaseOrdersAreDirty() ||
      this.checkCdfsAreDirty();
  }

  @handleErrors()
  async editCdf(cdf?: SiteCdfModel) {
    if (!cdf) {
      cdf = {id: "", system: "portico", buildings: []};
      this.site.cdfs.push(cdf);
    }

    const result = await SiteCdfDialogComponent.showCreateDialog(this.dialogService, cdf);

    if (result.id) {
      Object.assign(cdf, result);

      const flags = this.workflowService.workflowConfig.flags;
      if (!flags["importCdfDdl"] && !flags["importCdfTl"]) {
        this.workflowService.setWorkflowFlag("importCdfTl", true);
      }
    } else {
      const index = this.site.cdfs.indexOf(cdf);

      if (index >= 0) {
        this.site.cdfs.splice(index, 1);
      }
    }
  }

  @handleErrors()
  async setK2siteName(): Promise<any> {
    const result = await SelectK2SiteNameComponent.showSelectK2SiteNameDialog(
      this.dialogService,
      {k2Sites: this.k2Sites},
    );

    if (result.id && result.name) {
      this.site.k2Name = result.name;
      this.selectedK2Site = result.id;
    }
  }

  public importContactsClosed(event: any): void {
    const result = event["value"] as UploaderDialogResult;
    if (result.responses.length) {
      this.loadContacts();
    }
  }

  @handleErrors()
  async editSiteContact(type: string) {
    const trimmedType = type.replace("Contact", "");

    const result = await ModifyContactDialogComponent.showContactModal(
      this.dialogService,
      {
        targetType: trimmedType,
        targetLevel: "site",
        targetId: this.site.id,
        contactService: this.contactService,
      },
    );

    if (!result.changed) return;
    this.site[type] = result.contact;
  }

  @handleErrors()
  async download() {
    await this.httpDownloadService.downloadXHR("/api/sites/export-contacts", {siteId: this.site.id});
  }
}
